import Immutable from 'immutable';
import {findColorByRgb, INK_COLORS} from '~/constants/InkConstants';
import {COLOR_MODES} from '~/constants/UploadConstants';
import {EFFECTS} from '~/constants/TextEffectConstants';
import {MAX_NAMES_AND_NUMBERS_PER_SIZE} from '~/constants/NamesAndNumbersConstants';
import {
  ArtIcon,
  NamesIcon,
  NumbersIcon,
  TextIcon,
  UploadIcon
} from '~/data/records/icons';
import {isBlack, isWhite} from '~/data/accessors/color';
import Color from '~/data/records/Color';
import SpotColor from '~/data/records/SpotColor';
import NameAndNumber from '~/data/records/NameAndNumber';
import {makeSpotColor} from '~/utils/colors';
import {
  mapClipartColorFromRFE,
  mapSpotColorToTransparent
} from '~/utils/clipart';

export function deserializeIcons(icons) {
  const result = icons.reduce((acc, iconData) => {
    const icon = deserializeIcon(iconData);
    return icon ? acc.set(icon.id, icon) : acc;
  }, Immutable.OrderedMap());
  return result;
}

export function deserializeIcon(icon) {
  if (icon.fgcolor) {
    const [r, g, b, transparent] = icon.fgcolor;
    icon.color = transparent ? INK_COLORS.None : findColorByRgb({r, g, b});
  }
  if (icon.effect) {
    icon.effectType = icon.effect.type;
    icon.effectSetting = icon.effect.setting;
    icon.effectName = getEffectName(icon.effect.type, icon.effect.setting);
  }
  if (icon.outline) {
    const [r, g, b] = icon.outline.color;
    icon.outlineColor = findColorByRgb({r, g, b});
    icon.outlineWidth = parseFloat(icon.outline.width);
    if (icon.outline.clearFill) {
      icon.color = INK_COLORS.None;
    }
  }

  switch (icon.type) {
    case 'CLIPART':
      return deserializeArtIcon(icon).set('wasSaved', true);
    case 'TEXT':
      return new TextIcon(icon).set('wasSaved', true);
    case 'UPLOAD':
      return deserializeUploadIcon(icon).set('wasSaved', true);
    case 'NAMES':
      return new NamesIcon(icon);
    case 'NUMBERS':
      return new NumbersIcon(icon);
  }
}

function checkAvailability(arr, val) {
  const value = val || '0';
  return arr.some(arrVal => {
    return value === arrVal;
  });
}

function getEffectName(effectType, effectSetting) {
  const effect = EFFECTS.find(e => {
    const isType = e.type.toLowerCase() === effectType;
    const isSetting = checkAvailability(e.values, effectSetting);
    return isType && isSetting;
  });
  return effect.name;
}

function deserializeArtIcon(iconData) {
  const colors = (iconData.colors || [])
    .map(color => {
      // Backward compatibility: handle designs with old colors format
      if (!('original' in color)) {
        const inkColor = findColorByRgb(mapClipartColorFromRFE(color));
        return makeSpotColor(inkColor);
      }

      return new SpotColor({
        original: mapClipartColorFromRFE(color.original),
        palette: mapClipartColorFromRFE(color.palette),
        user: mapClipartColorFromRFE(color.user)
      });
    })
    .map(spotColor => {
      // Backward compatibility: sets white (original) spot colors to transparent (user) when the flag is set
      if (iconData.transparent) {
        return mapSpotColorToTransparent(spotColor);
      }
      return spotColor;
    });
  // Backward compatibility: sets blackwhite to true/false if property is missing
  if (iconData.transparent === undefined) {
    const isBlackAndWhite =
      colors.length <= 2 &&
      colors.every(c => {
        return isBlack(c.palette) || isWhite(c.palette);
      });
    iconData.blackwhite = isBlackAndWhite;
  }

  // Backward compatibility: If a clipart is blackwhite
  // Set original & palette of the First SpotColor to Black.
  if (iconData.blackwhite) {
    colors[0] = new SpotColor({
      original: INK_COLORS.Black,
      palette: INK_COLORS.Black,
      user: iconData.color
    });
  }

  // Backward compatibility: sets transparency to true/false if property is missing
  if (iconData.transparent === undefined) {
    iconData.transparent = iconData.colors.some(c => {
      return c.user.transparent;
    });
  }

  // Backward compatibility: sets transparency to true if multicolor clipart is made single color
  const singleColor = iconData.grayscale || iconData.singleColor;
  if (colors.length > 1 && singleColor && !iconData.blackwhite) {
    iconData.transparent = true;
  }
  return new ArtIcon({
    ...iconData,
    colors: Immutable.List(colors),
    originalHeight: iconData.height,
    originalWidth: iconData.width,
    singleColor
  });
}

function deserializeUploadIcon(icon) {
  const spotColors = (icon.spotColors || icon.colors || []).map(
    color => new SpotColor(color)
  );
  if (!icon.invalid) {
    return new UploadIcon({
      ...icon,
      backgroundColor: deserializeBackgroundColor(icon),
      colorMode: deserializeColorMode(icon),
      spotColors: Immutable.List(spotColors)
    });
  }
}

export function deserializeColorMode({colorMode, imageType}) {
  const imageTypeMap = {
    fullColor: COLOR_MODES.PHOTO,
    monochrome: COLOR_MODES.MONOCHROME,
    spotColor: COLOR_MODES.SPOT
  };
  return colorMode || imageTypeMap[imageType] || null;
}

function deserializeBackgroundColor({backgroundColor, colors}) {
  if (backgroundColor) {
    return new Color(backgroundColor);
  }
  if (colors) {
    const savedBackgroundColor = colors.find(
      color => color.background === true
    );
    const savedOriginalColor =
      savedBackgroundColor && savedBackgroundColor.original;
    return savedOriginalColor ? new Color(savedOriginalColor) : null;
  }
  return null;
}

export function deserializeNamesAndNumbers(items, data) {
  const {quantities} = (data && data.quoteData) || {};
  const list = items.filter(isValidNameAndNumber).reduce((list, item) => {
    return (
      data &&
      list.push(
        new NameAndNumber({...item, ...{productId: data.products[0].colorId}})
      )
    );
  }, Immutable.List());

  return namesAndNumbersFromSizes(quantities, list, items);
}

export function namesAndNumbersFromSizes(quantities, list, items) {
  if (!quantities || list.size === 0) return list;
  Object.keys(quantities).map(colorId => {
    for (var productSize in quantities[colorId]) {
      const itemCount = items.filter(item => item.size === productSize).length;
      const quantitiesWithoutPers =
        quantities[colorId][productSize] - itemCount;
      const validRange =
        quantitiesWithoutPers > 0 &&
        quantitiesWithoutPers < MAX_NAMES_AND_NUMBERS_PER_SIZE;
      if (!Number.isNaN(quantitiesWithoutPers) && validRange) {
        new Array(quantitiesWithoutPers).fill().map(() => {
          list = list.push(
            new NameAndNumber({productSize, productId: colorId})
          );
        });
      }
    }
  });
  return list;
}

export const isValidNameAndNumber = nameAndNumber =>
  nameAndNumber.name || nameAndNumber.number;
